/**
 * stale-chunk-rescue — automatic recovery for stale-shell chunk-load failures.
 *
 * Problem: when an old cached index.html is served (from a stale SW or HTTP
 * cache) alongside a newly activated SW that precaches only the new hashed
 * chunks, the lazy-import calls in the old shell will fail with network errors
 * because the old chunk URLs are gone. On iPhone PWA there is no hard-refresh,
 * and AutoUpdater's apply path only fires once a detection flag flips — which
 * may not have happened yet when a chunk load fails.
 *
 * Vite emits a window event "vite:preloadError" whenever a dynamically-imported
 * chunk fails to fetch. This module listens for that event and:
 *
 * - On the FIRST failure (within the guard window): runs the same
 *   purgeCachesAndReload() sequence as the Reload button so recovery is
 *   automatic. Sets sessionStorage["proof-chunk-rescue-at"] as a guard to
 *   prevent reload loops.
 *
 * - On a SECOND failure within the guard window (loop protection): does NOT
 *   reload again. Instead, calls flagExternalNeedRefresh() so AutoUpdater
 *   schedules a clean apply, and logs the stuck state.
 *
 * Guard window: 5 minutes. If more than 5 minutes have elapsed since the last
 * rescue attempt, the guard is reset and a fresh rescue is allowed.
 *
 * Registered from entry.client.tsx as a module side-effect.
 */

import { purgeCachesAndReload, RELOAD_FLAG_KEY } from "./reload";
import { flagExternalNeedRefresh } from "./version-probe";

/** sessionStorage key for the guard timestamp (ms since epoch as a string). */
const GUARD_KEY = "proof-chunk-rescue-at";

/** Guard window: 5 minutes in milliseconds. */
const GUARD_WINDOW_MS = 5 * 60 * 1000;

/** Detect if a rescue is already in-flight (set by purgeCachesAndReload). */
function isReloadingNow(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/** Return the timestamp of the last rescue attempt, or null if none / expired. */
function getGuardTimestamp(): number | null {
  try {
    const raw = sessionStorage.getItem(GUARD_KEY);
    if (!raw) return null;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return null;
    return ts;
  } catch {
    return null;
  }
}

function setGuardTimestamp(ts: number): void {
  try {
    sessionStorage.setItem(GUARD_KEY, String(ts));
    // eslint-disable-next-line no-restricted-syntax -- best-effort: reload-guard timestamp is SW rescue bookkeeping, not user data; unavailable storage is a no-op (Rule 8 documented degradation).
  } catch {
    // sessionStorage may be unavailable — not critical.
  }
}

/**
 * Handle a vite:preloadError event.
 *
 * Exported for unit tests; register via registerStaleChunkRescue() in production.
 */
export function handlePreloadError(event: Event): void {
  // If a reload is already in flight, do nothing — we are in the post-reload
  // boot, not in a stuck loop.
  if (isReloadingNow()) return;

  const now = Date.now();
  const guardTs = getGuardTimestamp();
  const guardActive = guardTs !== null && now - guardTs < GUARD_WINDOW_MS;

  if (!guardActive) {
    // First failure (or guard expired): attempt automatic rescue.
    // Prevent the default Vite error overlay if it has one.
    event.preventDefault();

    console.error("[proof SW] stale_chunk_rescue: first chunk-load failure detected", {
      event: "stale_chunk_rescue",
      attempt: "reload",
      at: new Date(now).toISOString(),
    });

    // Set the guard timestamp before the async reload so even a very fast
    // second failure won't slip through the window.
    setGuardTimestamp(now);

    // Run the shared flush+reload sequence (no updateServiceWorker callback
    // here — we do not have access to the vite-plugin-pwa hook outside React).
    void purgeCachesAndReload();
  } else {
    // Second (or later) failure within guard window — do not reload again.
    console.error("[proof SW] stale_chunk_rescue: chunk-load failure within guard window", {
      event: "stale_chunk_rescue_stuck",
      attempt: "flag_toast",
      guardTs,
      now,
      at: new Date(now).toISOString(),
    });

    // Flip the external needRefresh flag so AutoUpdater runs the full
    // applyUpdate flush (SW skip-waiting + cache purge + fresh navigation).
    // This is the escape hatch if the direct rescue reload is somehow stuck.
    flagExternalNeedRefresh();
  }
}

/**
 * Register the stale-chunk rescue listener on the window.
 *
 * Idempotent — calling more than once is a no-op after the first registration.
 * Call from entry.client.tsx after other module-level side effects.
 */
let registered = false;

export function registerStaleChunkRescue(): void {
  if (typeof window === "undefined") return;
  if (registered) return;
  registered = true;

  window.addEventListener("vite:preloadError", handlePreloadError);
}
